import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import EmailNav from "./EmailNav";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Send, Users, MailOpen, MousePointerClick, UserMinus, Plus, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { formatET } from "@/lib/dates";

const Stat = ({ label, value, icon: Icon, accent }) => (
  <Card data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <CardContent className="p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${accent || "bg-primary/10 text-primary"}`}>
        <Icon size={18} />
      </div>
      <div>
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="text-2xl font-semibold leading-tight">{value}</div>
      </div>
    </CardContent>
  </Card>
);

const STATUS_BADGE = {
  draft: "bg-muted text-foreground",
  scheduled: "bg-amber-500/15 text-amber-500",
  sending: "bg-blue-500/15 text-blue-500",
  sent: "bg-emerald-500/15 text-emerald-500",
};

export default function EmailHubPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const navigate = useNavigate();

  const load = () => {
    setLoading(true);
    api.get("/email/overview")
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Failed to load overview"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const syncNow = async () => {
    setSyncing(true);
    try {
      const { data: r } = await api.post("/email/contacts/sync-from-leads");
      toast.success(`Synced: +${r.created} new, ${r.updated} updated`);
      load();
    } catch {
      toast.error("Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const cumulative = data?.cumulative || {};
  const openRate = cumulative.delivered ? Math.round((cumulative.unique_opens / cumulative.delivered) * 100) : 0;
  const clickRate = cumulative.delivered ? Math.round((cumulative.unique_clicks / cumulative.delivered) * 100) : 0;

  return (
    <div data-testid="email-hub-page">
      <EmailNav />
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <p className="text-sm text-muted-foreground">Overview of your email marketing performance.</p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={syncNow} disabled={syncing} data-testid="hub-sync-btn">
            <RefreshCw size={14} className={`mr-2 ${syncing ? "animate-spin" : ""}`} /> Sync from Leads
          </Button>
          <Button size="sm" onClick={() => navigate("/email/campaigns/new")} data-testid="hub-new-campaign-btn">
            <Plus size={14} className="mr-2" /> New Campaign
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-20" />)}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
            <Stat label="Active Contacts" value={data.contacts} icon={Users} accent="bg-blue-500/10 text-blue-500" />
            <Stat label="Total Sent" value={cumulative.sent} icon={Send} accent="bg-primary/10 text-primary" />
            <Stat label="Open Rate" value={`${openRate}%`} icon={MailOpen} accent="bg-emerald-500/10 text-emerald-500" />
            <Stat label="Click Rate" value={`${clickRate}%`} icon={MousePointerClick} accent="bg-amber-500/10 text-amber-500" />
            <Stat label="Unsubscribed" value={data.unsubscribed} icon={UserMinus} accent="bg-rose-500/10 text-rose-500" />
          </div>

          <Card>
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-base font-outfit">Recent Campaigns</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate("/email/campaigns")}>View all →</Button>
            </CardHeader>
            <CardContent className="space-y-2">
              {(data.recent_campaigns || []).length === 0 && (
                <div className="text-sm text-muted-foreground py-8 text-center">
                  No campaigns yet. <button onClick={() => navigate("/email/campaigns/new")} className="text-primary hover:underline">Create your first campaign →</button>
                </div>
              )}
              {(data.recent_campaigns || []).map((c) => (
                <button
                  key={c.id}
                  data-testid={`recent-campaign-${c.id}`}
                  onClick={() => navigate(`/email/campaigns/${c.id}`)}
                  className="w-full text-left p-3 rounded-lg border border-border hover:bg-accent/40 transition-colors flex items-center justify-between gap-3"
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm truncate">{c.name}</div>
                    <div className="text-xs text-muted-foreground truncate">{c.subject}</div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <Badge className={STATUS_BADGE[c.status] || "bg-muted"}>{c.status}</Badge>
                    {c.status === "sent" && (
                      <span className="text-xs text-muted-foreground hidden sm:inline">
                        {c.stats?.sent || 0} sent · {c.stats?.unique_opens || 0} opens
                      </span>
                    )}
                    <span className="text-xs text-muted-foreground hidden md:inline">{formatET(c.sent_at || c.created_at)}</span>
                  </div>
                </button>
              ))}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
